import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {FormComponent} from 'appShared/widgets';
import {FormInterface} from 'appShared/interfaces';
import {DialogActionsEnum, DialogConfigInterface} from 'appShared/components';

import {FundOrderSellForm} from 'appEntries/panel/forms';
import {
    AccountSubTypeEnum,
    CashAccountDetailedReportModel,
    CurrencyTypesEnum,
    FundOpenPositionsRowInterface,
    FundOrderBaseInterface,
    FundOrderPlacementMethodEnum,
    FundOrdersService,
    FundOrderTypesEnum,
    FundProductInterface,
    OperatorInterface,
    ReportExtensions,
    TradingAccountDetailedReportModel
} from '@spa/api';

import {Router} from '@angular/router';
import {
    FundCreateOrderComponent,
    FundOrderSellProductSelector,
    FundOrderSellQuantitySelector
} from 'appAssets/descriptors';

import {PanelPaths} from 'appShared/paths';
import {AlertsService} from 'appShared/services';
import {SelectOptionInterface} from 'appShared/components';
import {Subscription} from 'rxjs';
import {AbstractControl, Validators} from '@angular/forms';
import {FundOrderMessages} from 'appAssets/messages';
import {OnlyDigitsValidator} from '@spa/validators';
import {Helper} from 'appShared/classes';

@Component({
    selector: 'app-fund-order-sell',
    templateUrl: './fund-order-sell.component.html',
    styleUrls: ['./fund-order-sell.component.scss']
})
export class FundOrderSellComponent extends FundCreateOrderComponent<FundOrderSellComponent> implements OnInit, OnDestroy, FormInterface {
    public readonly account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;
    private readonly openPositionsReportRows: FundOpenPositionsRowInterface[];
    private readonly products: FundProductInterface[];
    public readonly operator: OperatorInterface;
    private readonly panelPaths: typeof PanelPaths;

    private quantityControl: AbstractControl;
    private productControl: AbstractControl;

    public readonly isMutualFundAccount: boolean;

    public productCurrencySign: string;
    public availableQuantityText: string;
    public availableQuantity: number;
    public quantityNotValidBoxText: string;
    public productPriceBoxText: string;
    public productPriceDate: string;
    public productPriceAmount: number;

    public calculatedAmount: string;
    public orderForm: FundOrderSellForm;

    constructor(
        public readonly ordersService: FundOrdersService,
        public readonly router: Router,
        public readonly alertsService: AlertsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
            products: FundProductInterface[],
            openPositionsReportRows: FundOpenPositionsRowInterface[],
            operator: OperatorInterface
        },
        public dialogRef: MatDialogRef<FundOrderSellComponent>,
    ) {
        super(data.account, data.operator, ordersService, alertsService, router, dialogRef);

        this.orderType = FundOrderTypesEnum.Sell;

        this.config = data.config;
        this.account = data.account;
        this.openPositionsReportRows = data.openPositionsReportRows;
        this.products = data.products;
        this.operator = data.operator;
        this.panelPaths = PanelPaths;

        this.isMutualFundAccount = this.data.account
            ? this.data.account.AccountSubType === AccountSubTypeEnum.MutualFund : false;

        this.productPriceAmount = 0;
        this.availableQuantity = 0;
        this.quantityNotValidBoxText = '';
    }

    private getInputModel(): FundOrderBaseInterface | null {
        const productCode = this.orderForm.form.value[FundOrderSellProductSelector];
        const openPosition = this.openPositionsReportRows.find(position => position.productCode === productCode);

        const quantityValue = Helper.getAmountValue(this.orderForm.form.value[FundOrderSellQuantitySelector]);

        let amount: number;
        let quantity: number;

        switch (this.account.AccountSubType) {
            case AccountSubTypeEnum.MutualFund:
                amount = 0;
                quantity = quantityValue;
                break;
            case AccountSubTypeEnum.InstrumentCertificate:
                amount = Helper.getAmountValue(this.calculatedAmount);
                quantity = parseInt(quantityValue.toString(), 10);
                break;
            default:
                amount = null;
                quantity = null;
                break;
        }

        if (isNaN(amount) || isNaN(quantity)) {
            return null;
        }

        const model: FundOrderBaseInterface = {
            fee: 0,
            price: 0,
            interest: 0,
            cleanPrice: 0,
            accountId: this.account.accountId,
            accountReferenceId: this.account.accountReferenceId,
            orderType: FundOrderTypesEnum.Sell,
            orderPlacementMethod: FundOrderPlacementMethodEnum.Manual,
            productSymbol: openPosition.productCode,
            productType: openPosition.productType,
            amount,
            quantity,
            currency: openPosition.currency,
            accountHolderId: this.account.ownerId,
            accountHolderName: this.account.ownerName,
            createdByUserId: this.operator.user_id,
            createdByUser: [this.operator.first_name, this.operator.last_name].join(' ')
        };

        return model;
    }

    private initFormControls(): void {
        this.quantityControl = this.orderForm.form.get(FundOrderSellQuantitySelector);
        this.productControl = this.orderForm.form.get(FundOrderSellProductSelector);

        this.availableQuantityText = this.isMutualFundAccount ? 'Number of shares' : 'Number of units';

        if (this.productControl) {
            const subscription = this.productControl.valueChanges.subscribe(value => this.handleProductControlOnChange(value));
            this.orderSubscriptions.push(subscription);
        }

        if (this.quantityControl) {
            const subscription = this.quantityControl.valueChanges.subscribe(value => this.onValueChanged(value));
            this.orderSubscriptions.push(subscription);
        }

        this.orderForm.controls.product.options = this.initProductsControlOptions(this.openPositionsReportRows);

        this.setAmountControlValidators();
    }

    private manageProductPriceData(): void {
        if (!this.model.product.sellPrice) {
            this.cleanFormData(FundOrderMessages.FundOrderSellProductPriceBoxNoPricesText.message);

            return;
        }

        this.productCurrencySign = Helper.getCurrencySign(this.model.product.currency);
        this.productPriceAmount = this.model.product.sellPrice.value;
        this.productPriceDate = this.model.product.sellPrice.priceDate;
        this.productPriceBoxText = `${FundOrderMessages.FundOrderSellProductPriceBoxText.message} ${Helper.formatDate(this.productPriceDate)}`;
    }

    private setAmountControlValidators(): void {
        const amountControl = this.orderForm.form.get(FundOrderSellQuantitySelector);

        if (amountControl) {
            switch (this.account.AccountSubType) {
                case AccountSubTypeEnum.MutualFund:
                    amountControl.setValidators([Validators.required]);
                    break;
                case AccountSubTypeEnum.InstrumentCertificate:
                    amountControl.setValidators([Validators.required]);
                    break;
                default:
                    break;
            }

            amountControl.updateValueAndValidity();
        }
    }

    private manageInvalidQuantity(value: any): void {
        this.quantityNotValidBoxText = '';

        if (!value) {
            return;
        }

        value = Helper.getAmountValue(value);

        if (!value) {
            const errorItemText = this.isMutualFundAccount ? 'Shares' : 'Units';

            this.quantityNotValidBoxText = FundOrderMessages.InvalidNumberOfSharesUnits.message.replace('<<ref>>', errorItemText);

            return;
        }

        const isValidQuantity = value > 0 && this.availableQuantity > 0 && this.availableQuantity >= value;

        if (!isValidQuantity) {
            if (this.isMutualFundAccount) {
                this.quantityNotValidBoxText = FundOrderMessages.FundOrderSellQuantityInsufficientAccountSubTypeMutualFundText.message;
            } else {
                this.quantityNotValidBoxText = FundOrderMessages.FundOrderSellQuantityInsufficientAccountSubTypeInstrumentCertificateText.message;
            }
        } else {
            this.quantityNotValidBoxText = '';
        }
    }

    private handleQuantityControlOnChange(value: number): void {
        if (!value) {
            this.calculatedAmount = '0';

            return;
        }

        value = Helper.getAmountValue(value);
        this.calculatedAmount = ReportExtensions.toDecimalString(value * this.productPriceAmount);
    }

    private handleProductControlOnChange(value: string): void {
        this.cleanFormData('');

        const openPosition = this.openPositionsReportRows.find(position => position.productCode === value);

        if (!openPosition) {
            return;
        }

        this.availableQuantity = openPosition.availableProductQuantity;

        const product = this.products.find(productItem => productItem.code === openPosition.productCode);

        if (!product) {
            return;
        }

        super.init(product, openPosition, null);

        this.manageProductPriceData();
    }

    private cleanFormData(warningBoxText: string): void {
        this.availableQuantity = 0;
        this.quantityControl.setValue('');
        this.quantityNotValidBoxText = '';
    }

    private initProductsControlOptions(positions: FundOpenPositionsRowInterface[]): SelectOptionInterface[] {
        return positions.map((position: FundOpenPositionsRowInterface) => ({
            label: `${position.productName} - ${position.productCode}`,
            value: position.productCode
        }));
    }

    ngOnInit(): void {
        this.initForm();

        this.productCurrencySign = Helper.getCurrencySign(CurrencyTypesEnum.Euro);
    }

    ngOnDestroy(): void {
        this.orderSubscriptions.forEach((subscription: Subscription) => subscription && subscription.unsubscribe());
    }

    initForm(): void {
        this.orderForm = new FundOrderSellForm();

        this.orderForm.initForm(this.isMutualFundAccount);

        this.initFormControls();
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    validateForm(): boolean {
        return [
            !this.model.invalid,
            (this.quantityNotValidBoxText.trim() === ''),
            this.orderForm.validateForm(this.getFormMessages)
        ].indexOf(false) < 0;
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.startLoading(() => {
                        this.confirmOrder();
                    });

                }
                break;
        }
    }

    getPrice(): number {
        return this.model.product.sellPrice.value;
    }

    getInterest(): number {
        return this.model.product.sellPrice.accruedInterest;
    }
}
